import type { RealtimeGame, RealtimeState } from "../realtime";
import { createRng } from "../rng";

/**
 * One button, sixty steps a second — the game the alarm floor is furthest from
 * being able to carry, and the third on the realtime contract.
 *
 * A room can drive four ticks a second. This wants sixty, so the gap is not one
 * of degree: there is no version of the server-authoritative design that runs
 * this at all. It is here to find out whether anything in the contract quietly
 * assumed a slow game, and the answer turned out to be no — the same `step`,
 * the same log, the same `verifyRun`, with a bigger number in one field.
 *
 * ## Why there are no floats in a game about gravity
 *
 * Position and speed are whole numbers of world units, a thousand-odd of which
 * make up the height of the screen, rather than pixels or fractions. Doubles
 * would in fact be reproducible — IEEE 754 addition is exact and the same
 * everywhere — but "in fact reproducible" is a thing to have to keep being
 * right about across three games and every future change, whereas integers are
 * a thing nobody has to think about again.
 *
 * ## Where the pipes are
 *
 * Nowhere. They are derived from the seed and how far the world has scrolled,
 * in the same way Nibble's apples and Tumble's bag are: nothing to store, so
 * nothing a client could disagree with the server about.
 */

/**
 * The screen, in world units. Everything below is in these.
 *
 * Taller than it is wide, which is both what the original of this game was and
 * what the screens it gets played on are. The world was landscape first and
 * that was simply the wrong shape: scaled to fit a phone it filled 45% of the
 * glass and left the rest black, against 80–89% for this one. The width is not
 * a rule of the game — nothing collides with it — but it is how much warning a
 * player gets, so it is the same for both of them, like everything else here.
 */
export const FLIT_HEIGHT = 2400;
export const FLIT_WIDTH = 1800;

export const FLIT_RATE = 60;
/** Two minutes. Nobody survives it, which is rather the point. */
export const FLIT_MAX_STEPS = 7200;

/**
 * The bird never moves sideways — the world moves past it.
 *
 * A third of the way across, so what is left is the run-up: sixty-five steps,
 * a little over a second, between a pipe appearing and having to be through it.
 */
export const FLIT_BIRD_X = 500;
export const FLIT_BIRD_RADIUS = 60;

export const FLIT_GRAVITY = 1;
/** Falling any faster than this is unreadable rather than difficult. */
export const FLIT_TERMINAL = 44;
export const FLIT_FLAP = -26;

/*
 * How fast the world goes by, how thick a pipe is, and how far apart they are.
 *
 * These three were tuned together rather than picked, because the first guess
 * made a game that could not really be flown. A pipe 240 wide at 10 units a
 * step keeps the bird inside it for 36 steps — well over half a second — and
 * gravity moves it further in that time than the gap is tall, so getting
 * through depended on entering at exactly the right height with exactly the
 * right speed. Sweeping the parameters showed it up plainly: of thirty
 * settings of the computer opponent's aim, three survived. That is not a
 * difficult game, it is a game with one line through it.
 *
 * Twenty a step through a pipe 180 wide is fifteen steps — a quarter of a
 * second — and twenty-six of the same thirty settings then survive. The rhythm
 * is unchanged at a pipe every 1.7 seconds; what changed is that there is now
 * more than one way to fly it.
 */
export const FLIT_SCROLL = 20;
export const FLIT_PIPE_WIDTH = 180;
export const FLIT_PIPE_SPACING = 2080;
/** A moment of calm before the first one, so a child can find the button. */
export const FLIT_FIRST_PIPE = 5200;
export const FLIT_GAP = 780;
/** How close to the top or bottom a gap may be placed. */
export const FLIT_GAP_MARGIN = 300;

export const FLIT_PIPE_SCORE = 10;

export interface FlitState extends RealtimeState {
	kind: "flit";
	/** The bird's height, 0 at the top of the screen. */
	y: number;
	/** Downward speed per step. Negative is rising. */
	velocity: number;
	/** How far the world has scrolled, which is where every pipe is. */
	distance: number;
	/** Pipes gone by. The score is this and nothing else. */
	passed: number;
}

/** The only thing a player can do. */
export type FlitInput = { flap: true };

/** The middle of the nth pipe's gap. Derived, never stored. */
export function flitGap(seed: number, index: number): number {
	const rng = createRng((seed + index * 2_654_435_761) >>> 0);
	const span = FLIT_HEIGHT - 2 * FLIT_GAP_MARGIN - FLIT_GAP;
	return FLIT_GAP_MARGIN + FLIT_GAP / 2 + rng.int(span + 1);
}

/** Where the nth pipe's left edge is on screen, once the world has scrolled. */
export function flitPipeX(index: number, distance: number): number {
	return FLIT_FIRST_PIPE + index * FLIT_PIPE_SPACING - distance;
}

/**
 * How many pipes are wholly behind the bird.
 *
 * From the bird's trailing edge rather than its centre, so a pipe counts as
 * passed at the moment it stops being able to touch it. Measuring from the
 * centre leaves a sliver — three steps here — in which the bird is still inside
 * a pipe this says it is past, and anything steering by `flitNextPipe` spends
 * that sliver aiming at the wrong gap.
 */
export function flitPassed(distance: number): number {
	const behind = FLIT_BIRD_X - FLIT_BIRD_RADIUS + distance - FLIT_FIRST_PIPE - FLIT_PIPE_WIDTH;
	if (behind < 0) return 0;
	return Math.floor(behind / FLIT_PIPE_SPACING) + 1;
}

/**
 * The pipe the bird is about to meet — exactly the count of the ones behind it,
 * since they are numbered in the order they arrive.
 *
 * Worth stating separately because the obvious version is wrong: rounding up
 * from the bird's centre switches to the next pipe the moment that centre
 * crosses the leading edge, with the bird still most of a pipe's width from
 * being through. Anything steering by that aims elsewhere for the stretch that
 * matters most, which is what made the first computer opponent unflyable.
 */
export const flitNextPipe = flitPassed;

/**
 * Which pipes are worth testing against — the one ahead and its neighbours.
 *
 * Checking every pipe ever would be a loop that grows with the run, sixty times
 * a second, on both the device and the server. Three is more than can possibly
 * touch a bird sixty units wide with five hundred between gaps.
 */
function nearbyPipes(distance: number): number[] {
	const nearest = Math.floor((distance + FLIT_BIRD_X - FLIT_FIRST_PIPE) / FLIT_PIPE_SPACING);
	return [nearest - 1, nearest, nearest + 1].filter((index) => index >= 0);
}

function hitsAPipe(seed: number, y: number, distance: number): boolean {
	for (const index of nearbyPipes(distance)) {
		const left = flitPipeX(index, distance);
		/*
		 * No overlap across, so the gap does not matter.
		 *
		 * Strictly, so that edges exactly touching are through rather than into.
		 * That is the kinder reading, and it is also the one that agrees with
		 * `flitPassed` — with the comparison the other way round there is a
		 * single step on which a pipe has been counted as passed and can still
		 * end the run, which is the sort of thing a child would be quite right
		 * to call cheating.
		 */
		if (FLIT_BIRD_X + FLIT_BIRD_RADIUS <= left) continue;
		if (FLIT_BIRD_X - FLIT_BIRD_RADIUS >= left + FLIT_PIPE_WIDTH) continue;

		const middle = flitGap(seed, index);
		if (y - FLIT_BIRD_RADIUS < middle - FLIT_GAP / 2) return true;
		if (y + FLIT_BIRD_RADIUS > middle + FLIT_GAP / 2) return true;
	}
	return false;
}

export const flit: RealtimeGame<FlitState, FlitInput> = {
	id: "flit",
	rate: FLIT_RATE,
	maxSteps: FLIT_MAX_STEPS,

	create(seed) {
		return {
			kind: "flit",
			step: 0,
			seed,
			over: false,
			score: 0,
			y: Math.floor(FLIT_HEIGHT / 2),
			velocity: 0,
			distance: 0,
			passed: 0,
		};
	},

	parseInput(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		return (raw as { flap?: unknown }).flap === true ? { flap: true } : null;
	},

	step(state, input) {
		if (state.over) return state;

		// A flap replaces whatever the bird was doing rather than adding to it,
		// which is what makes the button feel like a button: two taps in quick
		// succession should not fire it into the ceiling.
		const flapped = input ? FLIT_FLAP : state.velocity;
		const velocity = Math.min(flapped + FLIT_GRAVITY, FLIT_TERMINAL);

		const distance = state.distance + FLIT_SCROLL;
		// The ceiling stops the bird rather than killing it. Hitting the roof is
		// not a mistake a child understands as one, and every version of this
		// game that punishes it is the version nobody plays twice.
		const y = Math.max(FLIT_BIRD_RADIUS, state.y + velocity);
		const passed = flitPassed(distance);

		const next: FlitState = {
			...state,
			step: state.step + 1,
			y,
			velocity,
			distance,
			passed,
			score: passed * FLIT_PIPE_SCORE,
		};

		if (y + FLIT_BIRD_RADIUS >= FLIT_HEIGHT) return { ...next, y, over: true };
		if (hitsAPipe(state.seed, y, distance)) return { ...next, over: true };
		return next;
	},
};

/**
 * Where the bird ends up in `steps` if it never flaps again.
 *
 * Two numbers rather than whole states: this runs up to a hundred times on
 * every one of sixty steps a second, and building a hundred objects to find out
 * one height would be the most expensive thing in the file by a distance.
 */
function coast(y: number, velocity: number, steps: number): number {
	let height = y;
	let speed = velocity;
	for (let n = 0; n < steps; n++) {
		speed = Math.min(speed + FLIT_GRAVITY, FLIT_TERMINAL);
		height = Math.max(FLIT_BIRD_RADIUS, height + speed);
	}
	return height;
}

/**
 * How close the next gap has to be, in world units, before Robo flies at it.
 *
 * This is the flying game's version of Zoom's `ROBO_REACTION`, and it is most
 * of Robo's fallibility, on purpose. The previous flyer predicted its arrival
 * height from any distance and flapped the moment the answer was low — which
 * made it perfect: it survived every one of the 7,200 steps and banked the
 * maximum score on every seed, so a child could never beat it, only draw it.
 * Reacting only when the pipe is close means a gap high above the cruise line
 * sometimes cannot be climbed to in the steps that are left, and how often
 * that happens depends on where the seed put the gaps — which is what makes
 * racing Robo feel like racing somebody.
 *
 * Tuned empirically together with the beat and the cruise line: at these
 * settings Robo dies mid-run on roughly five seeds in six, anywhere from a
 * handful of pipes to nearly the full run, rather than surviving everything
 * (too high) or dying at the first pipe (too low).
 */
export const FLYER_REACTION = 575;

/**
 * Robo reconsiders the button every few steps rather than sixty times a
 * second. The quantisation is the rest of the fallibility: a decision taken up
 * to four steps late arrives at the gap a little lower and a little faster
 * than the prediction said, and those small errors are what turn a near miss
 * into a hit on some seeds and not on others.
 */
const FLYER_BEAT = 5;

/**
 * Where Robo drifts between pipes — low, because a bird can always climb but
 * can never dive. Errors that leave it below the gap are the recoverable kind;
 * cruising high turned every slightly-low prediction into an unrecoverable
 * float over the top lip. Low also means the high gaps are the hard ones,
 * which is where the reaction distance does its work.
 */
const FLYER_CRUISE = FLIT_HEIGHT - 475;

/**
 * Robo flying: cruise until the next pipe is close, then fly at its gap.
 *
 * Three earlier versions of this did not work, and each failed in a way worth
 * writing down.
 *
 * The first flapped whenever the bird's *current* height was below a chosen
 * height in the gap. One flap lifts it by a third of the gap, so a rule blind
 * to its own momentum overshoots by that much every time and the bird
 * oscillates through a band taller than the hole it is aiming at. Reading the
 * height a beat ahead instead — where the bird's speed is already carrying it
 * — is what shrinks that band until it fits.
 *
 * The second flapped when carrying on would end the run within half a second.
 * That is a fine way to avoid the pipe you are at and no way at all to get to
 * the next one: by the time a gap is close enough to be seen that way, a bird
 * that needs to be eight hundred units higher cannot get there.
 *
 * The third predicted its exact arrival height at the gap from any distance
 * and flapped whenever the answer was low. It flew perfectly — every seed,
 * the whole two minutes, the maximum score — and a perfect Robo is a design
 * bug, not an achievement; see `FLYER_REACTION`. Worse, that trick only works
 * reconsidered every single step: decided on a beat, a marginal prediction
 * thirty steps out is answered with a flap that shifts the arrival by several
 * hundred units, always upward, which for a bird that cannot dive is the
 * unrecoverable direction.
 *
 * So Robo holds a height line a beat at a time — the gap once it is close,
 * the cruise line before that — aiming a little below the gap's middle
 * because the bird falls faster than it rises, so the room below the centre
 * is worth less than the room above it. Every error this makes is small and
 * low, the recoverable kind, and whether the climb fits in the steps that are
 * left is the question the reaction distance exists to sometimes answer "no".
 */
export function flitFlyer(state: FlitState): FlitInput | null {
	if (state.step % FLYER_BEAT !== 0) return null;

	const ahead = flitNextPipe(state.distance);
	const away = flitPipeX(ahead, state.distance) - FLIT_BIRD_X - FLIT_BIRD_RADIUS;
	// The height it flies at: the next gap once that is close enough to react
	// to, the cruise line the rest of the time.
	const aim = away > FLYER_REACTION ? FLYER_CRUISE : flitGap(state.seed, ahead) + FLIT_GAP / 5;

	return coast(state.y, state.velocity, FLYER_BEAT) > aim ? { flap: true } : null;
}
